import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactSignatureCanvas from 'react-signature-canvas';
import { Button } from '../UI/Button';

export type SignatureCanvasProps = {
  /** PNG em base64 (com ou sem prefixo data:image/png;base64,). */
  onConfirm: (pngBase64: string) => void;
  onCancel?: () => void;
  className?: string;
  disabled?: boolean;
};

const defaultCanvas = { width: 480, height: 220 };

export const SignatureCanvas: React.FC<SignatureCanvasProps> = ({
  onConfirm,
  onCancel,
  className = '',
  disabled = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sigRef = useRef<InstanceType<typeof ReactSignatureCanvas>>(null);
  const [size, setSize] = useState(defaultCanvas);

  useEffect(() => {
    const el = containerRef.current;
    if (!el)
    {
      return;
    }

    const measure = () => {
      const w = Math.max(280, Math.floor(el.getBoundingClientRect().width));
      setSize((prev) => {
        if (prev.width === w)
        {
          return prev;
        }

        return { width: w, height: defaultCanvas.height };
      });
    };

    measure();
    const ro = new ResizeObserver(() => {
      measure();
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, []);

  const handleClear = useCallback(() => {
    sigRef.current?.clear();
  }, []);

  const handleUndo = useCallback(() => {
    const pad = sigRef.current?.getSignaturePad();
    if (!pad)
    {
      return;
    }

    const data = pad.toData();
    if (!data.length)
    {
      return;
    }

    data.pop();
    pad.fromData(data);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!sigRef.current || sigRef.current.isEmpty())
    {
      return;
    }

    try
    {
      const trimmed = sigRef.current.getTrimmedCanvas();
      const dataUrl = trimmed.toDataURL('image/png');
      onConfirm(dataUrl);
    }
    catch
    {
      const fallback = sigRef.current.toDataURL('image/png');
      onConfirm(fallback);
    }
  }, [onConfirm]);

  return (
    <div className={`flex flex-col gap-3 ${className}`.trim()}>
      <p className="text-sm text-slate-600">Desenhe sua assinatura na área abaixo.</p>
      <div
        ref={containerRef}
        className="w-full rounded-md border border-slate-300 bg-white touch-none"
        style={{ touchAction: 'none' }}
      >
        <ReactSignatureCanvas
          key={size.width}
          ref={sigRef}
          penColor="#0f172a"
          minWidth={0.8}
          maxWidth={2.5}
          velocityFilterWeight={0.7}
          canvasProps={{
            width: size.width,
            height: size.height,
            className: 'block w-full max-w-full h-auto cursor-crosshair',
          }}
          clearOnResize={false}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={handleClear} disabled={disabled}>
          Limpar
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={handleUndo} disabled={disabled}>
          Desfazer
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={disabled}>
            Cancelar
          </Button>
        ) : null}
        <Button type="button" variant="primary" size="sm" onClick={handleConfirm} disabled={disabled}>
          Confirmar
        </Button>
      </div>
    </div>
  );
};
